// React
import { FunctionComponent, JSXElementConstructor, ReactElement, useCallback } from "react";
// Components
import { Page, PageConfiguration } from "@fyrstain/fhir-front-library";
import { MenuItem } from "@fyrstain/fhir-front-library";
// Translation
import { useTranslation } from "react-i18next";
// Authentication
import UserService from "../../services/UserService";
// Navigation
import { toPublicUrl } from "../../services/PublicUrl";

const AcesoPage: FunctionComponent<{
    // The title of the page
    titleKey?: string;
    // The loading state of the page
    loading?: boolean;
    // The content of the page
    children?: ReactElement<any, string | JSXElementConstructor<any>> | undefined;
    // If the page needs login or not
    needsLogin: boolean;
}> = (props) => {

    /////////////////////////////////
    //        NAVIGATION           //
    /////////////////////////////////

    const { i18n, t } = useTranslation();

    /////////////////////////////////
    //           METHODS           //
    /////////////////////////////////

    /*
    **
    **  This function is used to handle the login of the user.
    **  If the user is not logged in and the menu item needs login, it will redirect to the login page.
    **
    */
    const handleLogin = useCallback((config: MenuItem) => {
        if (config.needsLogin) {
            if (!UserService.isAuthenticated()) {
                UserService.doLogin();
            }
        }
    }, []);

    const handleLangChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        i18n.changeLanguage(event.target.value);
    };

    const fullConfig: PageConfiguration = {
        // Translation
        language: t,
        navigationBarConfigs: {
            // Application logo
            homeLink: toPublicUrl('/Home'),
            logoLink: toPublicUrl('/assets/temporaryAcesoLogo.png'),
            alt: t("a11y.acesoLogo"),
            // Authentication            
            authentication: {
                handleLogin: handleLogin,
                token: UserService.getKC().token,
                doLogin: UserService.doLogin,
                doLogout: UserService.doLogout,
                isAuthenticated: () => UserService.isAuthenticated() || false,
                getUserName: () => UserService.getUsername(),
            },
            // the menu items with their subItems who contains the navigation to the differents pages
            menuItems: [
                {
                    title: t("navbar.items.carecircle"),
                    subItems: [
                    {
                        title: t("navbar.items.viewcarecircles"),
                        link: toPublicUrl("/CareteamPage"),
                        needsLogin: true,
                    },
                    ],
                },
                {
                    title: t("navbar.items.communicationlog"),
                    subItems: [
                        {
                            title: t("navbar.items.createnote"),
                            link: toPublicUrl("/create-note"),
                            needsLogin: true,
                        },
                        {
                            title: t("navbar.items.viewnotes"),
                            link: toPublicUrl("/NotesPage"),
                            needsLogin: true,
                        },
                    ],
                },
            {
                title: t("navbar.items.pathway"),
                subItems: [
                    {
                        title: t("navbar.items.stays"),
                        link: toPublicUrl("/Pathway/Stays"),
                        needsLogin: true,
                    },
                    {
                        title: t("navbar.items.visits"),
                        link: toPublicUrl("/Pathway/Visits"),
                        needsLogin: true,
                    },
                    {
                        title: t("navbar.items.appointments"),
                        link: toPublicUrl("/Pathway/Appointments"),
                        needsLogin: true,
                    },
                ],
            },
            {
                title: t("navbar.items.patients"),
                link: toPublicUrl("/PatientsList"),
                needsLogin: true,
            },
            ],
            // the user items 
            // Admin, Login, Logout are the default items
            dropDownItems: [],
            // customItems: <ServerUrlField />
        },
        // the title of the page
        titleKey: props.titleKey,
        // the loading state of the page
        loading: props.loading,
        // the content of the page
        children: props.children,
        // if the page needs login or not
        needsLogin: props.needsLogin,
        footerConfigs: {
            languages: {
                default: i18n.language,
                onChange: handleLangChange,
                options: [
                    {
                        label: t("language.english"),
                        value: "en"
                    },
                    {
                        label: t("language.french"),
                        value: "fr"
                    }
                ]
            },
            logo: [
                {
                    src: "https://fyrstain.com/wp-content/uploads/2022/10/Logo_fyrstain_horyzontal.svg",
                    alt: "Horizontal logo type",
                    href: toPublicUrl("/Home")
                },
                ...(process.env.REACT_APP_DISPLAY_CLIENT_LOGO === 'true' ?
                    [
                        {
                            src: "https://fyrstain.com/wp-content/uploads/2022/10/Logo_fyrstain_horyzontal.svg",
                            alt: "Horizontal logo type",
                            href: toPublicUrl("/Home")
                        },
                        {
                            src: toPublicUrl("/assets/client_logo.jpg"),
                            alt: "HL7 Europe logo"
                        }
                    ]
                    : [])
            ],
            items: [
                {
                    label: t('footer.items.about'),
                    href: toPublicUrl("/InProgress")
                },
                {
                    label: t('footer.items.contact'),
                    href: toPublicUrl("/InProgress")
                },
                {
                    label: t('footer.items.problemtracking'),
                    href: toPublicUrl("/InProgress")
                },
                {
                    label: t("footer.items.implementationguide"),
                    href: toPublicUrl("/ImplementationGuide"),
                },
            ]
        }
    };

    return <Page {...fullConfig} />;
};

export default AcesoPage;
